// /redux/slices/user/eventCelebrationGiftsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../utils/api'; // Your API client (e.g., an axios instance)

// Async thunk to fetch event celebration gifts from your Django backend.
// Make sure your Django backend exposes an endpoint like '/api/event-celebration-gifts/'
export const fetchEventCelebrationGifts = createAsyncThunk(
  'eventCelebrationGifts/fetchEventCelebrationGifts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/event-celebration-gifts/');
      return response.data; // Expected to be an array of product objects
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch event celebration gifts');
    }
  }
);

// Define the initial filter state matching your component's defaults
const initialFilters = {
  personalize: false,
  productTypes: { hoodies: false, sweatshirts: false },
  sizes: { XS: false, S: false, M: false, L: false, XL: false, '2XL': false, '3XL': false },
  colors: { black: false, green: false, yellow: false, red: false, purple: false, custom: false },
  fabric: { cotton: false, polyester: false, cottonPolyBlend: false },
  printMethod: { screenPrinting: false, dtg: false, dtf: false, embroidery: false, heatTransfer: false },
  printPlacement: { front: false, back: false, sleeve: false, hood: false },
  customization: { oneColor: false, fullColor: false, logoOnly: false },
  orientation: '',
  corners: '',
  designStyle: { inter: false, prata: false, protestRevolution: false, promo: false },
  priceRange: 100,
  rates: { five: false, four: false, three: false },
};

// The initial state: products and filteredProducts will be populated via the API
const initialState = {
  loading: false,
  error: null,
  products: [],
  filteredProducts: [],
  filters: initialFilters,
};

const eventCelebrationGiftsSlice = createSlice({
  name: 'eventCelebrationGifts',
  initialState,
  reducers: {
    // Update a top-level (boolean) filter (e.g. "personalize")
    setSimpleFilter(state, action) {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    // Update a nested filter (e.g. in productTypes, sizes, colors, etc.)
    setNestedFilter(state, action) {
      const { category, key, value } = action.payload;
      state.filters[category][key] = value;
    },
    // Update a radio filter (for orientation, corners, etc.)
    setRadioFilter(state, action) {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    // Update the range filter (price range)
    setRangeFilter(state, action) {
      state.filters.priceRange = action.payload;
    },
    // Reset all filters to the default state and display all products
    resetFilters(state) {
      state.filters = { ...initialFilters };
      state.filteredProducts = state.products;
    },
    // Apply filtering logic based on current filter settings
    applyFilters(state) {
      const { filters, products } = state;
      
      // Build arrays of selected filter options
      const selectedTypes = Object.entries(filters.productTypes)
        .filter(([_, value]) => value)
        .map(([key]) => key);
  
      const selectedSizes = Object.entries(filters.sizes)
        .filter(([_, value]) => value)
        .map(([key]) => key);
  
      const selectedRates = Object.entries(filters.rates)
        .filter(([_, value]) => value)
        .map(([key]) => {
          if (key === 'five') return 5;
          if (key === 'four') return 4;
          if (key === 'three') return 3;
          return null;
        })
        .filter((rate) => rate !== null);
  
      state.filteredProducts = products.filter((product) => {
        // Filter by product type if any are selected.
        if (selectedTypes.length > 0) {
          const prodTypeKey = product.type === 'hoodie' ? 'hoodies' : 'sweatshirts';
          if (!selectedTypes.includes(prodTypeKey)) return false;
        }
  
        // Filter by sizes if any are selected
        if (selectedSizes.length > 0) {
          if (!product.sizes.some((size) => selectedSizes.includes(size))) return false;
        }
  
        // Filter by price range
        if (product.price > filters.priceRange) return false;
  
        // Filter by rating if any are selected
        if (selectedRates.length > 0) {
          if (!selectedRates.includes(product.rating)) return false;
        }
  
        // Additional filters (e.g., colors, fabric) can be implemented here as needed
        return true;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEventCelebrationGifts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEventCelebrationGifts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
        state.filteredProducts = action.payload;
      })
      .addCase(fetchEventCelebrationGifts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSimpleFilter,
  setNestedFilter,
  setRadioFilter,
  setRangeFilter,
  resetFilters,
  applyFilters,
} = eventCelebrationGiftsSlice.actions;

export default eventCelebrationGiftsSlice.reducer;
